import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Warehouse, FilePdf, ClockCounterClockwise, PencilSimple, Warning, Camera, Plus, Trash, Tag } from '@phosphor-icons/react';
import { fmtDateFR } from '../utils/dates';
import { useAuth } from '../context/AuthContext';
import { MaterialFormModal } from '../components/materials/MaterialFormModal';
import { CategoryManagerModal } from '../components/categories/CategoryManagerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StockPhotoUploader = ({ materialId, onUploaded }) => {
  const ref = React.useRef(null);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Image trop lourde (max 10 Mo)'); return; }
    const fd = new FormData();
    fd.append('file', f);
    try {
      await axios.post(`${API_URL}/api/materials/catalog/${materialId}/photo`, fd, { withCredentials: true });
      toast.success('Photo ajoutee');
      onUploaded && onUploaded();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { e.target.value = ''; }
  };
  return (
    <div
      onClick={() => ref.current?.click()}
      data-testid={`stock-photo-add-${materialId}`}
      style={{ width: '48px', height: '48px', borderRadius: '8px', background: 'linear-gradient(135deg, #EEF1FC, #E2E8F7)', border: '1px dashed #9DB2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      title="Ajouter une photo"
    >
      <Camera size={18} color="#002FA7" />
      <input ref={ref} type="file" accept="image/*" onChange={handle} style={{ display: 'none' }} />
    </div>
  );
};

const MaterialsStockPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {material_id, quantity, note}
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState('month');
  const [periodValue, setPeriodValue] = useState('');
  const [onlyLow, setOnlyLow] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/materials/stock`, { withCredentials: true });
      setStocks(res.data);
    } catch (e) {
      toast.error('Erreur chargement stocks');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/materials/stock/history?limit=300`, { withCredentials: true });
      setHistory(res.data);
    } catch (e) {
      toast.error('Erreur historique');
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return stocks.filter((s) => {
      if (onlyLow && !s.low_stock) return false;
      if (!q) return true;
      return s.name.toLowerCase().includes(q) || (s.category || '').toLowerCase().includes(q);
    });
  }, [stocks, search, onlyLow]);

  const lowCount = stocks.filter((s) => s.low_stock).length;

  const handleSave = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API_URL}/api/materials/stock/${editing.material_id}`, {
        quantity: parseFloat(editing.quantity || 0),
        note: editing.note || '',
      }, { withCredentials: true });
      toast.success('Stock mis a jour');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteMaterial = async (material) => {
    if (!window.confirm(`Supprimer définitivement le matériau "${material.name}" ?\n\nCela retirera l'article du catalogue, son stock et toutes ses occurrences dans les listes de chantier.`)) return;
    try {
      await axios.delete(`${API_URL}/api/materials/catalog/${material.material_id}`, { withCredentials: true });
      toast.success('Matériau supprimé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleExport = () => {
    const defaultVal = period === 'month'
      ? new Date().toISOString().slice(0, 7)
      : String(new Date().getFullYear());
    const val = periodValue || defaultVal;
    const url = `${API_URL}/api/materials/stock/export/pdf?period=${period}&value=${val}`;
    window.open(url, '_blank');
  };

  const totalValueHT = isAdmin ? stocks.reduce((acc, s) => acc + (s.value_ht || 0), 0) : 0;
  const reasonLabels = { manual_edit: 'Maj manuelle', project_list_validation: 'Validation liste chantier' };

  return (
    <div data-testid="materials-stock-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}><Warehouse size={20} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Stocks matériaux</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#5E5F66' }}>
            {isAdmin ? 'Gérez les quantités disponibles et exportez l\'historique.' : 'Consultez et mettez à jour les stocks.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' }}>
          {isAdmin && (
            <button
              type="button"
              className="btn stock-bar-btn"
              data-testid="manage-stock-categories-btn"
              onClick={() => setShowCategoryManager(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #7C3AED', background: 'white', color: '#7C3AED', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
              title="Ajouter, renommer ou supprimer des catégories de matériaux"
            >
              <Tag size={14} weight="bold" /> Catégories
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-primary stock-bar-btn" onClick={() => setShowAddMaterial(true)} data-testid="stock-new-material-btn">
              <Plus size={16} weight="bold" /> Nouveau matériau
            </button>
          )}
          <button className="btn btn-secondary stock-bar-btn" onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }} data-testid="toggle-history-btn">
            <ClockCounterClockwise size={16} /> {showHistory ? 'Masquer' : 'Historique'}
          </button>
          {isAdmin && (
            <>
              <select
                className="form-input stock-bar-btn"
                value={period}
                onChange={(e) => { setPeriod(e.target.value); setPeriodValue(''); }}
                data-testid="stock-period-select"
              >
                <option value="month">Mois</option>
                <option value="year">Année</option>
              </select>
              {period === 'month' ? (
                <select
                  className="form-input stock-bar-btn"
                  value={periodValue || new Date().toISOString().slice(0, 7)}
                  onChange={(e) => setPeriodValue(e.target.value)}
                  data-testid="stock-month-select"
                >
                  {(() => {
                    const options = [];
                    const now = new Date();
                    const monthLabels = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                    for (let i = 0; i < 24; i++) {
                      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                      options.push(<option key={val} value={val}>{monthLabels[d.getMonth()]} {d.getFullYear()}</option>);
                    }
                    return options;
                  })()}
                </select>
              ) : (
                <select
                  className="form-input stock-bar-btn"
                  value={periodValue || String(new Date().getFullYear())}
                  onChange={(e) => setPeriodValue(e.target.value)}
                  data-testid="stock-year-select"
                >
                  {(() => {
                    const current = new Date().getFullYear();
                    return Array.from({ length: 5 }, (_, i) => current - i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ));
                  })()}
                </select>
              )}
              <button className="btn btn-primary stock-bar-btn" onClick={handleExport} data-testid="export-stock-pdf-btn">
                <FilePdf size={16} /> Export PDF
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        <input type="search" className="form-input" placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: '340px' }} />
        <button
          data-testid="filter-low-stock-btn"
          className={onlyLow ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
          onClick={() => setOnlyLow(!onlyLow)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Warning size={14} /> Stock bas {lowCount > 0 && <span style={{ background: onlyLow ? 'white' : '#C62828', color: onlyLow ? '#C62828' : 'white', borderRadius: '10px', padding: '1px 8px', fontSize: '0.75rem', fontWeight: '700' }}>{lowCount}</span>}
        </button>
      </div>

      {/* KPI banner */}
      {isAdmin && stocks.length > 0 && (
        <div data-testid="stock-total-banner" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div style={{ flex: 1, minWidth: '240px', padding: '16px 20px', background: 'linear-gradient(135deg, #002FA7, #3D5AFE)', color: 'white', borderRadius: '10px' }}>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.85, fontWeight: '700' }}>Valeur totale du stock</div>
            <div style={{ fontSize: '1.8rem', fontWeight: '800', marginTop: '4px' }}>{totalValueHT.toFixed(2)} EUR <span style={{ fontSize: '0.8rem', fontWeight: '400', opacity: 0.9 }}>HT</span></div>
            <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '2px' }}>{stocks.length} référence(s) en catalogue</div>
          </div>
          <div style={{ flex: 1, minWidth: '240px', padding: '16px 20px', background: 'white', border: '2px solid #E2E2E6', borderRadius: '10px' }}>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#5E5F66', fontWeight: '700' }}>Valeur TTC estimée</div>
            <div style={{ fontSize: '1.8rem', fontWeight: '800', marginTop: '4px', color: '#002FA7' }}>{stocks.reduce((acc, s) => acc + ((s.value_ht || 0) * (1 + (s.tva_rate || 20) / 100)), 0).toFixed(2)} EUR</div>
            <div style={{ fontSize: '0.8rem', color: '#5E5F66', marginTop: '2px' }}>Tous taux TVA confondus</div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>Chargement...</div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Photo</th>
                <th>Matériau</th>
                <th>Catégorie</th>
                <th style={{ textAlign: 'right' }}>Quantité</th>
                <th>Unité</th>
                <th style={{ textAlign: 'right' }} title="Surface couvrable au m² (qté ÷ coefficient /m²)">m² dispo.</th>
                <th>Dernière maj</th>
                {isAdmin && <th style={{ textAlign: 'right' }}>Valeur HT</th>}
                <th style={{ width: '160px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.material_id} data-testid={`stock-row-${s.material_id}`} style={s.low_stock ? { background: '#FFF3E0' } : undefined}>
                  <td>
                    {s.photo_url ? (
                      <img src={`${API_URL}${s.photo_url}?v=${s.material_id}`} alt={s.name}
                           onError={(e) => { e.currentTarget.style.display = 'none'; }}
                           style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E2E2E6' }} />
                    ) : isAdmin ? (
                      <StockPhotoUploader materialId={s.material_id} onUploaded={load} />
                    ) : (
                      <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: 'linear-gradient(135deg, #EEF1FC, #E2E8F7)', border: '1px solid #E2E2E6' }} />
                    )}
                  </td>
                  <td style={{ fontWeight: '600' }}>
                    {s.name}
                    {s.low_stock && (
                      <span data-testid={`low-badge-${s.material_id}`} style={{ marginLeft: '8px', background: '#E65100', color: 'white', borderRadius: '10px', padding: '1px 7px', fontSize: '0.7rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        <Warning size={10} weight="fill" /> BAS
                      </span>
                    )}
                  </td>
                  <td style={{ color: '#5E5F66' }}>{s.category || '-'}</td>
                  <td style={{ textAlign: 'right', fontWeight: '700', color: s.low_stock ? '#E65100' : (s.quantity <= 0 ? '#C62828' : '#2E7D32') }}>
                    {Math.round(s.quantity)}
                    {s.min_threshold > 0 && <div style={{ fontSize: '0.68rem', color: '#9A9AA0', fontWeight: '400' }}>seuil {Math.round(s.min_threshold)}</div>}
                  </td>
                  <td>{s.unit}</td>
                  <td style={{ textAlign: 'right', fontWeight: '600', color: (s.coefficient_m2 && s.coefficient_m2 !== 1) ? '#002FA7' : '#9A9AA0' }}>
                    {s.coefficient_m2 && s.coefficient_m2 !== 1 && s.coefficient_m2 > 0
                      ? `${(s.quantity / s.coefficient_m2).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} m²`
                      : '-'}
                  </td>
                  <td style={{ fontSize: '0.82rem', color: '#5E5F66' }}>
                    {s.updated_by_name ? (<>{s.updated_by_name}<br /><span style={{ fontSize: '0.75rem' }}>{fmtDateFR(s.updated_at)}</span></>) : '-'}
                  </td>
                  {isAdmin && <td style={{ textAlign: 'right' }}>{(s.value_ht || 0).toFixed(2)} EUR</td>}
                  <td>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" data-testid={`edit-stock-${s.material_id}`}
                              onClick={() => setEditing({ material_id: s.material_id, name: s.name, unit: s.unit, quantity: s.quantity, note: '' })}>
                        <PencilSimple size={14} /> Maj
                      </button>
                      {isAdmin && (
                        <button
                          className="btn btn-danger btn-sm"
                          data-testid={`delete-stock-${s.material_id}`}
                          onClick={() => handleDeleteMaterial(s)}
                          title="Supprimer définitivement ce matériau"
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 9 : 8} style={{ textAlign: 'center', padding: '32px', color: '#5E5F66' }}>Aucun matériau.</td></tr>
              )}
            </tbody>
          </table>
          {isAdmin && stocks.length > 0 && (
            <div style={{ padding: '10px 16px', background: '#F4F5F7', textAlign: 'right', fontWeight: '600' }}>
              Valeur totale du stock : {totalValueHT.toFixed(2)} EUR HT
            </div>
          )}
        </div>
      )}

      {/* History */}
      {showHistory && (
        <div className="card" style={{ marginTop: '16px', overflow: 'auto' }}>
          <div style={{ padding: '12px 16px', fontWeight: '700', borderBottom: '1px solid #E2E2E6' }}>Historique des mouvements</div>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Matériau</th>
                <th style={{ textAlign: 'right' }}>Delta</th>
                <th style={{ textAlign: 'right' }}>Qté après</th>
                <th>Motif</th>
                <th>Chantier</th>
                <th>Par</th>
              </tr>
            </thead>
            <tbody>
              {history.map((m) => (
                <tr key={m.id}>
                  <td style={{ fontSize: '0.82rem' }}>{fmtDateFR(m.created_at)}</td>
                  <td>{m.material_name}</td>
                  <td style={{ textAlign: 'right', fontWeight: '600', color: m.delta >= 0 ? '#2E7D32' : '#C62828' }}>{m.delta >= 0 ? '+' : ''}{Math.round(m.delta)}</td>
                  <td style={{ textAlign: 'right' }}>{Math.round(m.quantity_after)}</td>
                  <td>{reasonLabels[m.reason] || m.reason}</td>
                  <td>{m.project_name || '-'}</td>
                  <td>{m.user_name}</td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: '#5E5F66' }}>Aucun mouvement.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', width: '100%', maxWidth: '420px', padding: '22px' }}>
            <h3 style={{ margin: '0 0 10px 0' }}>Mettre à jour : {editing.name}</h3>
            <div className="form-group">
              <label className="form-label">Nouvelle quantité ({editing.unit}) *</label>
              <input data-testid="stock-qty-input" type="number" min="0" step="1" className="form-input" value={editing.quantity} onChange={(e) => setEditing({ ...editing, quantity: e.target.value })} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Note (optionnel)</label>
              <input className="form-input" value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} placeholder="ex: Inventaire depot" />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} data-testid="save-stock-btn">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      <MaterialFormModal
        show={showAddMaterial}
        onClose={() => setShowAddMaterial(false)}
        onSaved={() => {
          // After creating a new material from stocks, redirect to the
          // merged "Bibliothèque & Catalogue" tab on the Matériaux sub-tab.
          navigate('/projects?tab=bibliotheque_catalogue&sub=materiaux');
        }}
      />

      <CategoryManagerModal
        open={showCategoryManager}
        onClose={() => setShowCategoryManager(false)}
        kind="materiaux"
        onChange={() => load()}
      />
    </div>
  );
};

export default MaterialsStockPage;
